import { BaseEditor, Editor, Node, NodeEntry, Range, Transforms } from 'slate'
import { Registry } from '@react-registry'
import React from 'react'
import { useSlate } from 'slate-react'

import { deleteHead } from './deleteHead'
import { deleteTable } from './deleteTable'
import { elementCategories } from '../../elementCategories'
import { TableDeleteRowIcon } from '../../../utils/Icon'
import { cellType } from './mergeCells'
import { cloneDeep } from 'lodash'
import { getSpanningCell } from './helper'

const decrementRowSpan = (editor: BaseEditor, cellEntry: NodeEntry<Node>, transferSpanToVoidCell = false) => {
  const [cell, path] = cellEntry
  const newAttrs = cloneDeep(cell['attrs'])
  newAttrs.rowSpan--
  newAttrs['redactor-attributes'].rowspan = newAttrs.rowSpan

  if (newAttrs.rowSpan === 1) {
    delete newAttrs['redactor-attributes'].rowspan
    delete newAttrs.rowSpan
  }

  if (transferSpanToVoidCell) {
    newAttrs.void = false
    path[path.length - 2]++
  }

  Transforms.setNodes(editor, {
    attrs: newAttrs
  } as Partial<Node>, {
    at: path
  })
}

export const deleteRow = (event, { editor }) => {

  const tableEntry = Editor.above(editor, {
    match: (n) => {
      return n["type"] === 'table'
    },
    mode: 'lowest'
  })

  const cellEntry = Editor.above(editor, {
    match: (n) => {
      return cellType.includes(n["type"])
    },
    mode: 'lowest'
  })

  const rowEntry = Editor.above(editor, {
    match: (n) => {
      return n["type"] === 'tr'
    },
    mode: 'lowest'
  })

  const theadEntry = Editor.above(editor, {
    match: (n) => {
      return n["type"] === 'thead'
    },
    mode: 'lowest'
  })

  const tbodyEntry = Editor.above(editor, {
    match: (n) => {
      return n["type"] === 'tbody'
    },
    mode: 'lowest'
  })

  if (!tableEntry || !cellEntry || !rowEntry) return

  const rowCount = tableEntry[0]["attrs"]["rows"]

  if (rowCount === 1) {
    deleteTable(event, { editor })
    return
  }

  if (theadEntry && theadEntry[0].children.length === 1) {
    deleteHead(event, { editor })
    Transforms.setNodes(
      editor,
      {
        attrs: {
          ...tableEntry[0]["attrs"],
          rows: rowCount - 1
        }
      } as Partial<Node>,
      { at: tableEntry[1] }
    )
    return
  }
  if (tbodyEntry && tbodyEntry[0].children.length === 1) {
    Transforms.removeNodes(editor, {
      at: tbodyEntry[1]
    })

    Transforms.setNodes(
      editor,
      {
        attrs: {
          ...tableEntry[0]["attrs"],
          rows: rowCount - 1
        }
      } as Partial<Node>,
      { at: tableEntry[1] }
    )
    return
  }

  const cellsInCurrentRow = rowEntry[0].children.map((cell, index) => {
    return [cell, [...rowEntry[1], index]] as NodeEntry<Node>
  })

  const cellsToDecrementRowSpanFrom: NodeEntry<Node>[] = []
  const cellsFromWhichSpansNeedToBeTransferred: NodeEntry<Node>[] = []

  for (const [cell, path] of cellsInCurrentRow) {
    let { rowSpan } = cell?.['attrs'] ?? {}

    if (rowSpan && rowSpan > 1) {
      cellsFromWhichSpansNeedToBeTransferred.push([cell, path])
    }

    if (Editor.isVoid(editor, cell)) {

      const spanningCellEntry = getSpanningCell(editor, path)

      if (spanningCellEntry) {
        const [, spanningCellPath] = spanningCellEntry

        if (spanningCellPath.at(-1) === path.at(-1)) {
          cellsToDecrementRowSpanFrom.push(spanningCellEntry)
        }
      }
    }
  }

  for (const ne of cellsToDecrementRowSpanFrom) {
    decrementRowSpan(editor, ne)
  }

  for (const ne of cellsFromWhichSpansNeedToBeTransferred) {
    decrementRowSpan(editor, ne, true)
  }

  Transforms.removeNodes(editor, {
    at: rowEntry[1],
  })

  Transforms.setNodes(
    editor,
    {
      attrs: {
        ...tableEntry[0]["attrs"],
        rows: rowCount - 1
      }
    } as Partial<Node>,
    { at: tableEntry[1] }
  )



}
const DeleteRowButton = (props) => {
  const editor = useSlate()
  return (
    <React.Fragment>
      {props.children({
        handleMouseDown: deleteRow,
        isDisable: { func: deleteRowButtonDisabler, params: { editor } },
        additionalParams: { editor }
      })}
    </React.Fragment>
  )
}
const deleteRowButtonDisabler = ({ editor }) => {

  if (editor.selection && Range.isExpanded(editor.selection)) return true

  const [table] = Editor.nodes(editor, {
    match: (n) => {
      return n["type"] === 'table'
    },
    mode: 'lowest'
  })
  return !table
}
Registry.register(
  {
    iconName: <TableDeleteRowIcon />,
    IngressComponent: DeleteRowButton,
    category: elementCategories.TABLE_ACTION,
    toolbar: {
      inSlashToolbar: false,
    },
    canCustomize: false,
    title: 'Delete row',
    isContentStackElement: true
  },
  {
    id: 'table-delete-row',
    registry: 'v2.element'
  }
)
